import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, computed, effect, ElementRef, HostListener, inject, input, NgZone, OnDestroy, OnInit, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { blackLineRegex, blackLineSpan, ML2HtmlUtils, ProductionCommandsObjectOptions, ProductionCommandType, ProductionFieldName } from '@common/utils/ml-2-html-utils';
import { Store } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { FROALA_DEFAULT_TOOLBAR_ITEMS, INITIAL_TIME_STAMP, PRODUCTION_COMMAND_BUTTONS } from '@src/suite/common/enums/constants';
import { EditorActions } from '@src/suite/common/enums/enums';
import { MosEventService } from '@src/suite/common/services/mos-event.service';
import { ProductionCommandDialogComponent } from '@src/suite/common/st-full-screen-dialog/production-command-dialog/production-command-dialog';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { AnchorCommand, DurationCommand, InsertCommand, NotesCommand, ProductionCommand, ReadRateCommand, TakeCommand } from '@src/suite/common/store/story/models/story.models';
import { convertDurationToENPSClientFormat, convertToDurationFieldFormat } from '@src/suite/common/utils/timing-format';
import { parseXml } from '@src/suite/common/utils/xml-utils';
import FroalaEditor from 'froala-editor';
import { catchError, Subject, take } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 } from 'uuid';

enum BlackLinePosition {
    TOP = 'top',
    MIDDLE = 'middle',
    BOTTOM = 'bottom',
}

@Component({
    selector: 'st-rich-text-editor',
    templateUrl: 'st-rich-text-editor.html',
    styleUrls: ['st-rich-text-editor.scss'],
    host: {
        class: 'st-rich-text-editor',
    },
    encapsulation: ViewEncapsulation.None,
})
export class RichTextEditorComponent implements OnInit, OnDestroy {
    BlackLinePositionEnum = BlackLinePosition;
    private destroy$ = new Subject<void>();
    private mosEventService = inject(MosEventService);

    aboveBlackLine = viewChild<ElementRef<HTMLDivElement>>('aboveBlackLine');
    belowBlackLine = viewChild<ElementRef<HTMLDivElement>>('belowBlackLine');

    dialog = inject(MatDialog);
    ngZone = inject(NgZone);
    modalService = inject(ModalService);
    store = inject(Store);

    languageSettings = this.store.selectSignal(selectI18N);

    focused = output<FocusEvent>();
    mosClicked = output<string>();
    mosExternalClicked = output<string>();
    selectedTextChange = output<string>();
    contentChanged = output<EnpsField[]>();
    save = output<string>();

    currentCommandDialogRef: MatDialogRef<ProductionCommandDialogComponent, any> = null;

    public story = input<StorytellingStoryRecord>(null);
    public disabled = input(false);
    autoUpdate = input(false, {
        transform: coerceBooleanProperty,
    });

    public editor = signal<FroalaEditor>(null);
    public editorAboveBlackLineFocused = signal<boolean>(false);
    public editorBelowBlackLineFocused = signal<boolean>(false);
    public editorBelowBlackLine = signal<FroalaEditor>(null);

    currentEditor = computed(() => {
        return this.editorBelowBlackLineFocused() ? this.editorBelowBlackLine() : this.editor();
    });

    storyText = computed(() => {
        return this.content() + blackLineSpan + this.contentBelowBlackLine();
    });

    loadingData = signal(true);
    content = signal<string>(null);
    contentBelowBlackLine = signal<string>(null);
    initialMap = signal<Map<string, string>>(new Map());
    currentPosition = signal<BlackLinePosition>(BlackLinePosition.BOTTOM);
    public editorHeight: number = 0;

    options = {
        htmlAllowedEmptyTags: [
            'span.st-rich-text-editor--production-expander',
            'span.st-rich-text-editor--ProductionCommand-Expand-Icon',
            'span.st-rich-text-editor--production-details',
            'span.material-symbols-outlined',
        ],
        pluginsEnabled: ['link', 'url'],
        htmlAllowedAttrs: htmlAllowedAttrs,
        key: environment.froala_key,
        enter: FroalaEditor.ENTER_BR,
        events: {
            initialized: (e: FroalaEditor) => {
                const editor = e.getEditor();
                if (editor) {
                    if (this.disabled()) {
                        editor.edit.off();
                    }

                    this.applyEditorEventListeners(editor);

                    editor.events.on(
                        'focus',
                        event => {
                            this.editorAboveBlackLineFocused.set(true);
                            this.editorBelowBlackLineFocused.set(false);
                            this.focused.emit(event);
                            /**
                             * Froala is parsing the content and that is why we are setting the content on first focus.
                             * This way we are preventing misslieading differences betweeen the story text and the Froala content
                             */
                            this.setInitialContentMap();
                        },
                        true
                    );
                    editor.events.on(
                        'commands.before',
                        (cmd, param1, param2) => {
                            if (this.editorBelowBlackLineFocused()) {
                                this.editorBelowBlackLine().commands.exec(cmd, [param1, param2]);
                                return false;
                            }
                        },
                        true
                    );
                }
                this.editor.set(editor);
                this.setSaveButtonState(true);
            },
            /**
             * When a link is clicked inside the editor, the default behavior is to follow the link.
             * We prevent this by calling preventDefault() and open the link in a new tab instead.
             * This is done to prevent the user from leaving the story edit page.
             */
            click: clickEvent => {
                if (clickEvent.target && clickEvent.target.tagName.toLowerCase() === 'a') {
                    clickEvent.preventDefault();
                    const href = clickEvent.target.getAttribute('href');
                    if (href) {
                        window.open(href, '_blank');
                    }
                }
            },
            'paste.before': (clipboardEvent: ClipboardEvent) => {
                // Before pasting content, we need to check if the content contains links
                // If it does, we need to insert the content ourselves, otherwise the links will not be clickable
                const clipboardData = clipboardEvent.clipboardData;
                const pastedHtml = clipboardData.getData('Text')?.trim();
                const linkRegex = new RegExp(/<a.*>.*<\/a>/gi);
                if (pastedHtml && pastedHtml.match(linkRegex)) {
                    // Insert the content ourselves
                    this.editor().html.insert(pastedHtml);
                    // Return false to prevent the default paste behavior
                    return false;
                }
            },
        },
        toolbarButtons: {
            ...FROALA_DEFAULT_TOOLBAR_ITEMS,
        },
    };

    optionsBelowBlackLine = {
        ...this.options,
        events: {
            ...this.options.events,
            initialized: (e: FroalaEditor) => {
                const editor = e.getEditor();
                if (editor) {
                    editor.toolbar.hide();
                    if (this.disabled()) {
                        editor.edit.off();
                    }
                    this.applyEditorEventListeners(editor);
                    editor.events.on(
                        'focus',
                        event => {
                            this.editorAboveBlackLineFocused.set(false);
                            this.editorBelowBlackLineFocused.set(true);
                            this.focused.emit(event);
                            /**
                             * Froala is parsing the content and that is why we are setting the content on first focus.
                             * This way we are preventing misslieading differences betweeen the story text and the Froala content
                             */
                            this.setInitialContentMap();
                        },
                        true
                    );

                    this.editorBelowBlackLine.set(editor);
                }
            },
        },
    };

    constructor() {
        const productionButtons = [];
        PRODUCTION_COMMAND_BUTTONS.forEach(command => {
            productionButtons.push(command);
        });

        this.options = {
            ...this.options,
            toolbarButtons: {
                ...this.options.toolbarButtons,
                moreProductionCommands: {
                    buttons: productionButtons,
                    align: 'left',
                    buttonsVisible: 6,
                },
            },
        };

        effect(
            () => {
                if (this.editor() && this.editorBelowBlackLine()) {
                    if (this.disabled()) {
                        this.editor().edit.off();
                        this.editorBelowBlackLine().edit.off();
                    } else {
                        this.editor().edit.on();
                        this.editorBelowBlackLine().edit.on();
                    }
                    if (this.story() != null && this.story()?.text != null) {
                        const text = this.story()?.text?.trim();
                        const contents = text?.split(blackLineRegex);
                        this.content.set(contents?.[0] || '');
                        this.contentBelowBlackLine.set(contents?.[2] || '');
                    }
                    this.setSaveButtonState(!this.story()?.hasUnsavedChanges);
                }
            },
            {
                allowSignalWrites: true,
            }
        );
    }

    ngOnInit(): void {
        this.mosEventService
            .onMosItem()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ storyGuid, mosCommand }) => {
                if (this.story()?.Guid === storyGuid) {
                    this.addMosItemToStoryListener(mosCommand);
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener('click', ['$event.target'])
    onClick(element: Element) {
        this.hostClickListener(element);
    }

    @HostListener('dragstart', ['$event'])
    onDragStart(event: DragEvent) {
        event.dataTransfer?.setData('text/plain', (event.target as HTMLElement).id);
        event.dataTransfer!.dropEffect = 'move';
    }

    isInitialContentChanged: boolean = false;
    setInitialContentMap() {
        const storyGuid = this.story()?.Guid;
        if (storyGuid && !this.initialMap()?.has(storyGuid)) {
            this.isInitialContentChanged = false;
            this.initialMap().clear();
            this.initialMap().set(storyGuid, this.storyText() ? this.storyText() : '');
        }
    }

    resetContentState() {
        this.isInitialContentChanged = false;
        this.initialMap().clear();
    }

    ensureCursorVisibility(editor) {
        if (editor.core.hasFocus() && editor.browser.mozilla) {
            editor.events.disableBlur();
            setTimeout(() => {
                editor.$el.blur().focus();
                editor.events.enableBlur();
            }, 0);
        }
    }

    removeCommandObject(commandId: string) {
        const newStory = { ...this.story() };
        const parsedStoryText = parseXml(newStory.text, 'text/html');
        if (parsedStoryText?.body) {
            const mosItem = parsedStoryText.getElementById(commandId);
            if (mosItem) {
                mosItem.remove();
                newStory.text = `<html xmlns="http://www.w3.org/1999/xhtml"><body>${parsedStoryText.body.innerHTML}</body></html>`;
            }
            const textCommands = newStory.TextCommands;
            delete textCommands[commandId];
            newStory.TextCommands = textCommands;
            this.emitContentChanged([
                {
                    key: PSTFieldNames.PSTRichText,
                    value: newStory.text,
                },
                {
                    key: PSTFieldNames.TextCommands,
                    value: newStory.TextCommands,
                },
            ]);
        }
    }

    expandAllElements() {
        const content = this.storyText().replaceAll('expand_less', 'expand_more').replaceAll('--hidden', '');
        this.editor().html.set(content.split(blackLineRegex)[0] || '');
        this.editorBelowBlackLine().html.set(content.split(blackLineRegex)[2] || '');
    }

    hostClickListener(element: Element) {
        if (!element) return;

        // TODO: move this and dragstart to a mutation observer, for NED
        if (element.classList.contains('st-rich-text-editor--production-close')) {
            this.removeProductionObject(element);
            return;
        } else if (element.classList.contains('st-rich-text-editor--mos-open-external')) {
            const mosElement = element.closest('.st-rich-text-editor--mos');
            if (mosElement) {
                const mosId = mosElement.getAttribute('mosid');
                this.mosClickedEvent(mosElement);
                this.mosExternalClicked.emit(mosId);
                return;
            }
        }
        if (element.classList.contains('st-rich-text-editor--settings')) {
            const commandElementHtml = element.closest('.st-rich-text-editor--production');
            this.onProductionCommandEdit(commandElementHtml?.id);
            return;
        }
        const productionCommandExpand = element.closest('.st-rich-text-editor--production-expander');
        if (productionCommandExpand) {
            const expanderIcon = productionCommandExpand.textContent;
            productionCommandExpand.textContent = expanderIcon === 'expand_more' ? 'expand_less' : 'expand_more';
            const prodCommandParent = productionCommandExpand.closest('.st-rich-text-editor--production');
            const mosCommandParent = productionCommandExpand.closest('.st-rich-text-editor--mos');
            if (mosCommandParent) {
                this.toggleDetails(mosCommandParent, 'st-rich-text-editor--mos-details');
            } else if (prodCommandParent) {
                this.toggleDetails(prodCommandParent, 'st-rich-text-editor--production-details');
                this.toggleDetails(prodCommandParent, 'st-rich-text-editor--production-details--notes');
            }
            return;
        }

        const mosElement = element.closest('.st-rich-text-editor--mos');
        if (mosElement) {
            this.mosClickedEvent(mosElement);
        }
    }

    toggleDetails(parentElement, detailsSelector) {
        const detailsElement = parentElement.querySelector('.' + detailsSelector);
        if (detailsElement) {
            detailsElement.classList.toggle(detailsSelector + '--hidden');
        }
    }

    removeProductionObject(element: Element) {
        const modalRef = this.modalService.openModal(`${this.languageSettings().PSTWebConfirmDeletePart1} item?`, '', ModalResponseCodes.OK, ModalResponseCodes.CANCEL);
        modalRef.result
            .pipe(
                take(1),
                catchError(() => {
                    return [ModalResponseCodes.CANCEL];
                })
            )
            .subscribe(res => {
                if (res === ModalResponseCodes.OK) {
                    const mosElementParent = element.closest('.st-rich-text-editor--mos');
                    const commandElementParent = element.closest('.st-rich-text-editor--production');
                    const commandElementId = mosElementParent?.id || commandElementParent?.id;
                    this.removeCommandObject(commandElementId);
                }
            });
    }

    mosClickedEvent(mosElement: Element) {
        const parentId = mosElement.id;
        this.mosClicked.emit(parentId);
    }

    onInitialContentChanged(storyText?: string) {
        if (!this.isInitialContentChanged) {
            /**
             * On every BasicContent we are returning different production command id,
             * so we need to remove it before comparing the froala's content change event and initial content.
             */
            const content = storyText.replace(new RegExp('id="[^"]*"', 'g'), "id=''");
            const initialText = this.initialMap().get(this.story().Guid)?.replace(new RegExp('id="[^"]*"', 'g'), "id=''");

            if (content === initialText) {
                return;
            }
        }
        this.isInitialContentChanged = true;
    }

    onContentChange(event: string) {
        const content = this.editor().html.get() + blackLineSpan + this.editorBelowBlackLine().html.get();
        this.onInitialContentChanged(content);

        if (this.isInitialContentChanged) {
            this.story().hasUnsavedChanges = true;
            if (this.autoUpdate() === false) {
                this.setSaveButtonState(false);
            }
            this.content.set(event);
            this.updateStoryContent(content);
        }
    }

    updateStoryContent(content: string) {
        this.emitContentChanged([{ key: PSTFieldNames.PSTRichText, value: content }]);
    }

    emitContentChanged(fields: EnpsField[]) {
        if (this.autoUpdate() === false) {
            this.setSaveButtonState(false);
        }
        this.contentChanged.emit(fields);
    }

    onProductionCommandEdit(commandId: string) {
        const productionCommand = this.story()?.TextCommands?.[commandId];
        const parsedProdCommand = this.parseCommand(productionCommand, commandId);
        if (!parsedProdCommand) {
            return;
        }
        if (this.currentCommandDialogRef !== null && this.currentCommandDialogRef.getState() === MatDialogState.OPEN) {
            this.currentCommandDialogRef.close();
        }
        this.currentCommandDialogRef = this.dialog.open(ProductionCommandDialogComponent, {
            hasBackdrop: true,
            panelClass: 'st-production-command-dialog',
            data: { command: parsedProdCommand, editMode: true },
            autoFocus: true,
            delayFocusTrap: true,
        });
        this.currentCommandDialogRef.componentInstance.onUpdate.subscribe((command: ProductionCommand) => {
            if (!(this.story()?.Guid && command?.guid && this.story()?.TextCommands[command.guid])) {
                return;
            }

            let newStory: StorytellingStoryRecord = {};
            if (
                (command?.commandType === ProductionCommandType.DURATION && !command[command.commandType]) ||
                ((command?.commandType === ProductionCommandType.ANCHOR || command?.commandType === ProductionCommandType.READRATE || command?.commandType === ProductionCommandType.NOTES) &&
                    !command[ProductionFieldName.TEXT])
            ) {
                // Remove the command on edit when there is no value
                newStory = { ...this.story() };
                let parsedStoryText = parseXml(newStory.text, 'text/html');
                if (!parsedStoryText?.body) {
                    parsedStoryText = parseXml('<html xmlns="http://www.w3.org/1999/xhtml"><body><p></p></body></html>', 'text/html');
                }

                const productionItem = parsedStoryText.getElementById(command.guid);
                if (productionItem) {
                    productionItem.replaceWith('');
                }
                newStory.text = parsedStoryText.documentElement.outerHTML;
            } else {
                const textProdCommand = this.convertProductionCommandToText(command);
                if (!textProdCommand) {
                    return;
                }
                let textCommands = this.story().TextCommands;
                if (!textCommands) {
                    textCommands = {};
                }
                textCommands[command.guid] = textProdCommand.startsWith('[') && textProdCommand.endsWith(']') ? textProdCommand : `[${textProdCommand}]`;
                this.story().TextCommands = textCommands;
                newStory = { ...this.story() };
                let parsedStoryText = parseXml(newStory.text, 'text/html');
                if (!parsedStoryText?.body) {
                    parsedStoryText = parseXml('<html xmlns="http://www.w3.org/1999/xhtml"><body><p></p></body></html>', 'text/html');
                }

                const productionItemElement = ML2HtmlUtils.generateProductionCommandHtml(textProdCommand.replace(new RegExp('\r\n|\n', 'g'), '<br/>'), command.guid);
                if (productionItemElement) {
                    const productionItem = parsedStoryText.getElementById(command.guid);
                    if (productionItem) {
                        productionItem.replaceWith(productionItemElement);
                    }
                    newStory.text = parsedStoryText.documentElement.outerHTML;
                } else {
                    console.log('Failed to parse mos item');
                }
            }
            this.emitContentChanged([
                {
                    key: PSTFieldNames.PSTRichText,
                    value: newStory.text,
                },
                {
                    key: PSTFieldNames.TextCommands,
                    value: newStory.TextCommands,
                },
            ]);
        });
        this.currentCommandDialogRef.componentInstance.onDelete.subscribe(() => {
            this.removeCommandObject(commandId);
        });
    }

    onProductionCommandCreate(event: { productionCommadType: ProductionCommandType; pointerProductionCommand: number; guid: string }) {
        const productionCommand = this.initCommnad(event.productionCommadType);
        if (!productionCommand) {
            return;
        }

        if (this.currentCommandDialogRef !== null && this.currentCommandDialogRef.getState() === MatDialogState.OPEN) {
            this.currentCommandDialogRef.close();
        }
        this.currentCommandDialogRef = this.dialog.open(ProductionCommandDialogComponent, {
            hasBackdrop: true,
            panelClass: 'st-production-command-dialog',
            data: { command: productionCommand },
            autoFocus: true,
            delayFocusTrap: true,
        });

        this.currentCommandDialogRef.componentInstance.onInsert.subscribe((command: ProductionCommand) => {
            if (!this.story() || !command) {
                return;
            }
            const textProdCommand = this.convertProductionCommandToText(command);
            if (!textProdCommand) {
                return;
            }
            const newElId = v4();
            let textCommands = this.story().TextCommands;
            if (!textCommands) {
                textCommands = {};
            }
            textCommands[newElId] = textProdCommand.startsWith('[') && textProdCommand.endsWith(']') ? textProdCommand : `[${textProdCommand}]`;
            this.story().TextCommands = textCommands;
            const newStory = { ...this.story() };
            let parsedStoryText = parseXml(newStory.text, 'text/html');
            if (!parsedStoryText?.body) {
                parsedStoryText = parseXml('<html xmlns="http://www.w3.org/1999/xhtml"><body><p></p></body></html>', 'text/html');
            }

            const productionItemElement = ML2HtmlUtils.generateProductionCommandHtml(textProdCommand.replace(new RegExp('\r\n|\n', 'g'), '<br/>'), newElId);
            if (productionItemElement) {
                // handle it with the pointer and the html from the editor, not from the story record
                const content = this.currentEditor().html.get();

                event.pointerProductionCommand = content.substring(0, event.pointerProductionCommand).endsWith('[/PROD]') ? event.pointerProductionCommand + 12 : event.pointerProductionCommand;
                const partBeforePointer = content.substring(0, event.pointerProductionCommand);
                const findParentTag = (part: string): 'span' | 'p' | null => {
                    const spanIndex = part?.toLowerCase()?.lastIndexOf('<span');
                    const pIndex = part?.toLowerCase()?.lastIndexOf('<p');
                    if (spanIndex === -1 && pIndex === -1) {
                        return null;
                    }
                    return spanIndex > pIndex ? 'span' : 'p';
                };
                const parentTag = findParentTag(partBeforePointer);
                let updatedContent = '';
                if (parentTag) {
                    if (parentTag === 'span') {
                        updatedContent = `${partBeforePointer}</${parentTag}></p>${productionItemElement.outerHTML}<p><${parentTag}>${content.substring(event.pointerProductionCommand)}`;
                    } else {
                        updatedContent = `${partBeforePointer}</${parentTag}>${productionItemElement.outerHTML}<${parentTag}>${content.substring(event.pointerProductionCommand)}`;
                    }
                } else {
                    updatedContent = `${partBeforePointer}<p></p>${productionItemElement.outerHTML}<p></p>${content.substring(event.pointerProductionCommand)}`;
                }

                parsedStoryText = parseXml(updatedContent, 'text/html');

                if (this.editorBelowBlackLineFocused()) {
                    newStory.text = `${this.content()}${blackLineSpan}${parsedStoryText.body.innerHTML}`;
                } else {
                    newStory.text = `${parsedStoryText.body.innerHTML}${blackLineSpan}${this.contentBelowBlackLine()}`;
                }

                this.emitContentChanged([
                    {
                        key: PSTFieldNames.PSTRichText,
                        value: newStory.text,
                    },
                    {
                        key: PSTFieldNames.TextCommands,
                        value: newStory.TextCommands,
                    },
                ]);
            } else {
                console.log('Failed to parse production command item');
            }
        });
    }

    initCommnad(commandType: ProductionCommandType): ProductionCommand {
        switch (commandType) {
            case ProductionCommandType.ANCHOR:
            case ProductionCommandType.READRATE:
                return {
                    commandType: commandType,
                    guid: null,
                    [ProductionFieldName.TEXT]: '',
                } as AnchorCommand | ReadRateCommand;
            case ProductionCommandType.DURATION:
                return {
                    commandType: commandType,
                    guid: null,
                    [ProductionFieldName.DURATION]: null,
                } as DurationCommand;

            case ProductionCommandType.NOTES:
                return {
                    commandType: commandType,
                    guid: null,
                    [ProductionFieldName.TEXT]: '',
                } as NotesCommand;

            case ProductionCommandType.TAKE:
            case ProductionCommandType.INSERT:
                return {
                    commandType: commandType,
                    guid: null,
                    [ProductionFieldName.OPTION]: ProductionCommandsObjectOptions[ProductionCommandsObjectOptions.length - 1].title,
                    [ProductionFieldName.OPTION_DESCRIPTION]: null,
                    [ProductionFieldName.NAME]: null,
                    [ProductionFieldName.NUMBER]: null,
                    [ProductionFieldName.INCUE]: null,
                    [ProductionFieldName.OUTCUE]: null,
                    [ProductionFieldName.AT]: null,
                    [ProductionFieldName.TO]: null,
                    [ProductionFieldName.TIME_VALUE]: null,
                    [ProductionFieldName.TIME_FIELD]: ProductionFieldName.DURATION,
                } as TakeCommand | InsertCommand;

            default:
                throw new Error(`Unknown command type: ${commandType}`);
        }
    }

    parseCommand(command: string, guid?: string): ProductionCommand {
        if (command.endsWith(']')) command = command.substring(1, command.length - 1);
        if (command.startsWith('[')) command = command.substring(1);

        command = command.replace(new RegExp('\r\n|\n', 'g'), '<br/>');
        const lines = command.split('<br/>');
        const contentField = new Map<string, string>();

        for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            if (index === 0) {
                const splitHeader = line.split(':');
                for (const keyword in ProductionCommandType) {
                    const keywordUpper = keyword.toUpperCase();
                    if (splitHeader[0]?.toUpperCase().includes(keywordUpper)) {
                        contentField.set(ProductionFieldName.COMMAND_TYPE, ProductionCommandType[keywordUpper]);
                        let headerContent = splitHeader[1];
                        if (ProductionCommandType.INSERT === ProductionCommandType[keywordUpper] || ProductionCommandType.TAKE === ProductionCommandType[keywordUpper]) {
                            if (splitHeader.length === 1) {
                                headerContent = line.replace(keyword, '').trim();
                                contentField.set(ProductionFieldName.HEADER_CONTENT, headerContent);
                                break;
                            }

                            contentField.set(ProductionFieldName.HEADER_CONTENT, ProductionCommandsObjectOptions[ProductionCommandsObjectOptions.length - 1].title);
                            contentField.set(ProductionFieldName.OPTION_DESCRIPTION, headerContent);
                            break;
                        }
                        if (ProductionCommandType.DURATION === ProductionCommandType[keywordUpper]) {
                            headerContent = convertToDurationFieldFormat(headerContent);
                        }
                        contentField.set(ProductionFieldName.HEADER_CONTENT, headerContent);
                        break;
                    }
                    continue;
                }
            }

            const lineValus = line.split(':');
            const key = lineValus[0];
            let value = lineValus[1];

            if (key === ProductionFieldName.AT || key === ProductionFieldName.TO || key === ProductionFieldName.DURATION || key === ProductionFieldName.ITEM_TIME) {
                value = convertToDurationFieldFormat(value);
            }

            contentField.set(key, value);
        }

        const selectedTimeField = contentField.get(ProductionFieldName.DURATION) ? ProductionFieldName.DURATION : ProductionFieldName.ITEM_TIME;
        const selectedTimeValue = contentField.get(selectedTimeField);
        switch (contentField.get(ProductionFieldName.COMMAND_TYPE)) {
            case ProductionCommandType.ANCHOR:
            case ProductionCommandType.READRATE:
                return {
                    commandType: contentField.get(ProductionFieldName.COMMAND_TYPE),
                    guid: guid?.toString(),
                    [ProductionFieldName.TEXT]: contentField.get(ProductionFieldName.HEADER_CONTENT),
                } as AnchorCommand | ReadRateCommand;
            case ProductionCommandType.DURATION:
                return {
                    commandType: contentField.get(ProductionFieldName.COMMAND_TYPE),
                    guid: guid?.toString(),
                    [ProductionFieldName.DURATION]: contentField.get(ProductionFieldName.HEADER_CONTENT),
                } as DurationCommand;

            case ProductionCommandType.NOTES:
                return {
                    commandType: contentField.get(ProductionFieldName.COMMAND_TYPE),
                    guid: guid?.toString(),
                    [ProductionFieldName.TEXT]: contentField.get(ProductionFieldName.HEADER_CONTENT),
                } as NotesCommand;

            case ProductionCommandType.TAKE:
            case ProductionCommandType.INSERT:
                return {
                    commandType: contentField.get(ProductionFieldName.COMMAND_TYPE),
                    guid: guid?.toString(),
                    [ProductionFieldName.OPTION]: contentField.get(ProductionFieldName.HEADER_CONTENT),
                    [ProductionFieldName.OPTION_DESCRIPTION]: contentField.get(ProductionFieldName.OPTION_DESCRIPTION),
                    [ProductionFieldName.NAME]: contentField.get(ProductionFieldName.NAME),
                    [ProductionFieldName.NUMBER]: contentField.get(ProductionFieldName.NUMBER),
                    [ProductionFieldName.INCUE]: contentField.get(ProductionFieldName.INCUE),
                    [ProductionFieldName.OUTCUE]: contentField.get(ProductionFieldName.OUTCUE),
                    [ProductionFieldName.AT]: contentField.get(ProductionFieldName.AT),
                    [ProductionFieldName.TO]: contentField.get(ProductionFieldName.TO),
                    [ProductionFieldName.TIME_FIELD]: selectedTimeField,
                    [ProductionFieldName.TIME_VALUE]: selectedTimeValue,
                } as TakeCommand | InsertCommand;

            default:
                throw new Error(`Unknown command type: ${contentField.get(ProductionFieldName.COMMAND_TYPE)}`);
        }
    }

    convertProductionCommandToText(command: ProductionCommand): string {
        let textLinesArray = [];
        switch (command.commandType) {
            case ProductionCommandType.ANCHOR:
            case ProductionCommandType.READRATE:
                textLinesArray = command[ProductionFieldName.TEXT] ? [`${command.commandType}:${command[ProductionFieldName.TEXT]}`] : null;
                break;
            case ProductionCommandType.DURATION:
                // eslint-disable-next-line no-irregular-whitespace
                textLinesArray =
                    command[ProductionFieldName.DURATION] && command[ProductionFieldName.DURATION] !== INITIAL_TIME_STAMP
                        ? [`${command.commandType}:${convertDurationToENPSClientFormat(command[ProductionFieldName.DURATION])}`]
                        : null;
                break;

            case ProductionCommandType.NOTES:
                // eslint-disable-next-line no-irregular-whitespace
                textLinesArray = command[ProductionFieldName.TEXT] ? [`${command.commandType}:${command[ProductionFieldName.TEXT].replace(new RegExp('\r\n|\n', 'g'), ' ')}`] : null;
                break;

            case ProductionCommandType.TAKE:
            case ProductionCommandType.INSERT:
                textLinesArray = [
                    `${command.commandType}${
                        command[ProductionFieldName.OPTION] && command[ProductionFieldName.OPTION] !== ProductionCommandsObjectOptions[ProductionCommandsObjectOptions.length - 1].title
                            ? ' ' + command[ProductionFieldName.OPTION]
                            : `${
                                  command[ProductionFieldName.OPTION_DESCRIPTION] &&
                                  command[ProductionFieldName.OPTION] === ProductionCommandsObjectOptions[ProductionCommandsObjectOptions.length - 1].title
                                      ? ` :${command[ProductionFieldName.OPTION_DESCRIPTION]}`
                                      : ' :'
                              }`
                    }`,
                    `${command[ProductionFieldName.NAME] ? `${ProductionFieldName.NAME}:${command[ProductionFieldName.NAME]}` : ''}`,
                    `${command[ProductionFieldName.NUMBER] ? `${ProductionFieldName.NUMBER}:${command[ProductionFieldName.NUMBER]}` : ''}`,
                    `${command[ProductionFieldName.INCUE] ? `${ProductionFieldName.INCUE}:${command[ProductionFieldName.INCUE]}` : ''}`,
                    `${command[ProductionFieldName.OUTCUE] ? `${ProductionFieldName.OUTCUE}:${command[ProductionFieldName.OUTCUE]}` : ''}`,
                    `${
                        command[ProductionFieldName.AT] && command[ProductionFieldName.AT] !== INITIAL_TIME_STAMP
                            ? `${ProductionFieldName.AT}:${convertDurationToENPSClientFormat(command[ProductionFieldName.AT])}`
                            : ''
                    }`,
                    `${
                        command[ProductionFieldName.TO] && command[ProductionFieldName.TO] !== INITIAL_TIME_STAMP
                            ? `${ProductionFieldName.TO}:${convertDurationToENPSClientFormat(command[ProductionFieldName.TO])}`
                            : ''
                    }`,
                    `${
                        command[ProductionFieldName.TIME_VALUE] && command[ProductionFieldName.TIME_VALUE] !== INITIAL_TIME_STAMP
                            ? `${command[ProductionFieldName.TIME_FIELD]}:${convertDurationToENPSClientFormat(command[ProductionFieldName.TIME_VALUE])}`
                            : ''
                    }`,
                ];
                break;
            default:
                throw new Error(`Unknown command type: ${command.commandType}`);
        }
        return textLinesArray && textLinesArray.length > 0 ? `[${textLinesArray.filter(val => !!val.trim()).join('\r\n')}]` : '';
    }

    setSaveButtonState(disable: boolean) {
        // TODO: handle both editor and editorBelowBlackLine
        const saveButton = this.editor()?.$tb?.find('.fr-command[data-cmd="save"]');
        if (saveButton) {
            if (disable) {
                saveButton[0].classList.add('fr-disabled');
                saveButton[0].setAttribute('aria-disabled', 'true');
                saveButton[0].setAttribute('tabindex', '-1');
            } else {
                saveButton[0].classList.remove('fr-disabled');
                saveButton[0].removeAttribute('aria-disabled');
                saveButton[0].setAttribute('tabindex', '0');
            }
        }
    }

    insertProductionCommand(command: ProductionCommandType) {
        this.currentEditor().events.trigger('productionCommand', [command]);
    }

    applyEditorEventListeners(editor: FroalaEditor) {
        editor.events.on(
            'drop',
            ev => {
                const data = ev.originalEvent.dataTransfer.getData('text/plain');
                editor.markers.insertAtPoint(ev.originalEvent);
                const $marker = editor.$el.find('.fr-marker');
                $marker.replaceWith(FroalaEditor.MARKERS);
                editor.selection.restore();
                if (!editor.undo.canDo()) editor.undo.saveStep();

                const element = document.getElementById(data);
                if (element) {
                    editor.html.insert(element.outerHTML);
                    element.remove();
                }

                editor.undo.saveStep();
                ev.preventDefault();
                ev.stopPropagation();
                this.ensureCursorVisibility(this.editor());

                return false;
            },
            true
        );
        editor.events.on(
            'mouseup',
            () => {
                const selectedText = editor.selection.text().trim();
                if (selectedText) {
                    let prodCommand: boolean = false;
                    const selTextTest = window
                        .getSelection()
                        .toString()
                        .split('\n')
                        .reduce((acc, el) => {
                            if (el === '[PROD]') {
                                prodCommand = true;
                                return acc;
                            }
                            if (el === '[/PROD]') {
                                prodCommand = false;
                                return acc;
                            }
                            if (!prodCommand) {
                                acc.push(el);
                            }
                            return acc;
                        }, [])
                        .join('\n');
                    this.ngZone.run(() => {
                        this.selectedTextChange.emit(selTextTest);
                    });
                }
            },
            true
        );
        editor.events.on(
            'mousedown',
            () => {
                if (editor.selection.text()) {
                    editor.selection.clear();
                    this.ngZone.run(() => {
                        this.selectedTextChange.emit(editor.selection.text().trim());
                    });
                }
            },
            true
        );
        editor.events.on(
            'productionCommand',
            title => {
                this.ngZone.run(() => {
                    // Insert a unique HTML marker
                    // TODO: see if it can be done better by using the editor from the parameter
                    this.currentEditor().selection.restore();
                    if (!this.currentEditor().undo.canDo()) this.currentEditor().undo.saveStep();
                    let uniqueMarker = '<span id="unique-caret-marker"></span>';
                    this.currentEditor().html.insert(uniqueMarker);
                    let content = this.currentEditor().html.get();
                    const isuniqueMarkerPlacedInSpan = content.includes('<span>' + uniqueMarker + '</span>');
                    uniqueMarker = isuniqueMarkerPlacedInSpan ? '<span>' + uniqueMarker + '</span>' : uniqueMarker;
                    let caretPosition = content.indexOf(uniqueMarker);
                    if (caretPosition < 0) {
                        caretPosition = 0;
                    }
                    content = content.replace(uniqueMarker, '');
                    this.currentEditor().html.set(content);

                    this.onProductionCommandCreate({
                        productionCommadType: title.toUpperCase() as ProductionCommandType,
                        pointerProductionCommand: caretPosition,
                        guid: this.story().Guid,
                    });

                    this.currentEditor().undo.saveStep();
                    this.currentEditor().selection.save();
                    this.currentEditor().selection.restore();
                });
            },
            true
        );
        editor.events.on(
            EditorActions.SAVE,
            event => {
                this.save.emit(event);
            },
            true
        );
    }

    onArrowUpClick() {
        const aboveBlackLineEl = this.aboveBlackLine().nativeElement;
        const belowBlackLineEl = this.belowBlackLine().nativeElement;
        if (this.currentPosition() === BlackLinePosition.BOTTOM) {
            aboveBlackLineEl.classList.remove(BlackLinePosition.BOTTOM);
            belowBlackLineEl.classList.remove(BlackLinePosition.BOTTOM);
            aboveBlackLineEl.classList.add(BlackLinePosition.MIDDLE);
            this.currentPosition.set(BlackLinePosition.MIDDLE);
        } else if (this.currentPosition() === BlackLinePosition.MIDDLE) {
            aboveBlackLineEl.classList.remove(BlackLinePosition.MIDDLE);
            aboveBlackLineEl.classList.add(BlackLinePosition.TOP);
            this.currentPosition.set(BlackLinePosition.TOP);
        }
    }

    onArrowDownClick() {
        const aboveBlackLineEl = this.aboveBlackLine().nativeElement;
        const belowBlackLineEl = this.belowBlackLine().nativeElement;
        if (this.currentPosition() === BlackLinePosition.TOP) {
            aboveBlackLineEl.classList.remove(BlackLinePosition.TOP);
            aboveBlackLineEl.classList.add(BlackLinePosition.MIDDLE);
            this.currentPosition.set(BlackLinePosition.MIDDLE);
        } else if (this.currentPosition() === BlackLinePosition.MIDDLE) {
            aboveBlackLineEl.classList.remove(BlackLinePosition.MIDDLE);
            aboveBlackLineEl.classList.add(BlackLinePosition.BOTTOM);
            belowBlackLineEl.classList.add(BlackLinePosition.BOTTOM);
            this.currentPosition.set(BlackLinePosition.BOTTOM);
        }
    }

    addMosItemToStoryListener(mosCommand: string) {
        const newElId = v4();
        const newStory = { ...this.story() };

        const mosItemElement = ML2HtmlUtils.generateMosItemHtml(mosCommand, newElId);
        if (mosItemElement) {
            let textCommands = newStory.TextCommands;
            if (!textCommands) {
                textCommands = {};
            }
            textCommands[newElId] = mosCommand;
            newStory.TextCommands = textCommands;
            let parsedStoryText = parseXml(newStory.text, 'text/html');
            if (!parsedStoryText?.body) {
                parsedStoryText = parseXml('<html xmlns="http://www.w3.org/1999/xhtml"><body><p></p></body></html>', 'text/html');
            }
            this.currentEditor().selection.restore();
            if (!this.currentEditor().undo.canDo()) this.currentEditor().undo.saveStep();
            let uniqueMarker = '<span id="unique-caret-marker"></span>';
            this.currentEditor().html.insert(uniqueMarker);
            let content = this.currentEditor().html.get();
            const isuniqueMarkerPlacedInSpan = content.includes('<span>' + uniqueMarker + '</span>');
            uniqueMarker = isuniqueMarkerPlacedInSpan ? '<span>' + uniqueMarker + '</span>' : uniqueMarker;
            let caretPosition = content.indexOf(uniqueMarker);
            if (caretPosition < 0) {
                caretPosition = 0;
            }
            content = content.replace(uniqueMarker, '');
            this.currentEditor().html.set(content);
            caretPosition = content.substring(0, caretPosition).endsWith('[/MOS]') ? caretPosition + 12 : caretPosition;
            const partBeforePointer = content.substring(0, caretPosition);
            const findParentTag = (part: string): 'span' | 'p' | null => {
                const spanIndex = part?.toLowerCase()?.lastIndexOf('<span');
                const pIndex = part?.toLowerCase()?.lastIndexOf('<p');
                if (spanIndex === -1 && pIndex === -1) {
                    return null;
                }
                return spanIndex > pIndex ? 'span' : 'p';
            };
            const parentTag = findParentTag(partBeforePointer);
            let updatedContent = '';
            if (parentTag) {
                if (parentTag === 'span') {
                    updatedContent = `${partBeforePointer}</${parentTag}></p>${mosItemElement.outerHTML}<p><${parentTag}>${content.substring(caretPosition)}`;
                } else {
                    updatedContent = `${partBeforePointer}</${parentTag}>${mosItemElement.outerHTML}<${parentTag}>${content.substring(caretPosition)}`;
                }
            } else {
                updatedContent = `${partBeforePointer}<p></p>${mosItemElement.outerHTML}<p></p>${content.substring(caretPosition)}`;
            }

            parsedStoryText = parseXml(updatedContent, 'text/html');

            if (this.editorBelowBlackLineFocused()) {
                newStory.text = `${this.content()}${blackLineSpan}${parsedStoryText.body.innerHTML}`;
            } else {
                newStory.text = `${parsedStoryText.body.innerHTML}${blackLineSpan}${this.contentBelowBlackLine()}`;
            }

            this.emitContentChanged([
                {
                    key: PSTFieldNames.PSTRichText,
                    value: newStory.text,
                },
                {
                    key: PSTFieldNames.TextCommands,
                    value: newStory.TextCommands,
                },
            ]);
        } else {
            console.error('Failed to parse mos item');
        }
    }
}

const htmlAllowedAttrs = [
    'mosclass',
    'mosid',
    'accept',
    'accept-charset',
    'accesskey',
    'action',
    'align',
    'allowfullscreen',
    'allowtransparency',
    'alt',
    'aria-.*',
    'async',
    'autocomplete',
    'autofocus',
    'autoplay',
    'autosave',
    'background',
    'bgcolor',
    'border',
    'charset',
    'cellpadding',
    'cellspacing',
    'checked',
    'cite',
    'class',
    'color',
    'cols',
    'colspan',
    'content',
    'contenteditable',
    'contextmenu',
    'controls',
    'coords',
    'data',
    'data-.*',
    'datetime',
    'default',
    'defer',
    'dir',
    'dirname',
    'disabled',
    'download',
    'draggable',
    'dropzone',
    'enctype',
    'for',
    'form',
    'formaction',
    'frameborder',
    'headers',
    'height',
    'hidden',
    'high',
    'href',
    'hreflang',
    'http-equiv',
    'icon',
    'id',
    'ismap',
    'itemprop',
    'keytype',
    'kind',
    'label',
    'lang',
    'language',
    'list',
    'loop',
    'low',
    'max',
    'maxlength',
    'media',
    'method',
    'min',
    'mozallowfullscreen',
    'multiple',
    'muted',
    'name',
    'novalidate',
    'open',
    'optimum',
    'pattern',
    'ping',
    'placeholder',
    'playsinline',
    'poster',
    'preload',
    'pubdate',
    'radiogroup',
    'readonly',
    'rel',
    'required',
    'reversed',
    'rows',
    'rowspan',
    'sandbox',
    'scope',
    'scoped',
    'scrolling',
    'seamless',
    'selected',
    'shape',
    'size',
    'sizes',
    'span',
    'src',
    'srcdoc',
    'srclang',
    'srcset',
    'start',
    'step',
    'summary',
    'spellcheck',
    'style',
    'tabindex',
    'target',
    'title',
    'type',
    'translate',
    'usemap',
    'value',
    'valign',
    'webkitallowfullscreen',
    'width',
    'wrap',
    'mat-icon',
    'mat-icon-button',
];
